// Firebase web SDK init for SEVRIC Battery Care.
//
// All values come from VITE_FIREBASE_* env vars. We REQUIRE every value at
// build time so a misconfigured staging build can't silently point at prod.
// Values are public-by-design — security lives in Firestore rules + custom
// claims, not in keeping these secret. Source of truth is portal/public/
// js/firebase-config.js — keep .env in lockstep with that file.
import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";

function required(name) {
  const v = import.meta.env[name];
  if (!v || /^REPLACE_ME/.test(v)) {
    throw new Error(
      `Missing Firebase config: ${name}. Add it to .env and rebuild.`,
    );
  }
  return v;
}

const firebaseConfig = {
  apiKey:            required("VITE_FIREBASE_API_KEY"),
  authDomain:        required("VITE_FIREBASE_AUTH_DOMAIN"),
  projectId:         required("VITE_FIREBASE_PROJECT_ID"),
  storageBucket:     required("VITE_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: required("VITE_FIREBASE_SENDER_ID"),
  appId:             required("VITE_FIREBASE_APP_ID"),
};

export const app       = initializeApp(firebaseConfig);
// SEVRIC Chat needs the FirebaseApp instance to mint the Supabase JWT via
// httpsCallable. Export both the named `app` const (legacy) and a getter
// (for code that wants a fail-loud accessor).
export function getFirebaseApp() { return app; }

// App Check — reCAPTCHA Enterprise. The site key is public-by-design (it's
// embedded in the client bundle and shipped to every browser). Real
// protection comes from the matching `APP_CHECK_ENFORCE=true` env on the
// functions side, which rejects any callable invocation without a valid token.
// If VITE_APP_CHECK_SITE_KEY is missing we skip init — useful for local dev.
const APP_CHECK_SITE_KEY = import.meta.env.VITE_APP_CHECK_SITE_KEY;
if (APP_CHECK_SITE_KEY && !/^REPLACE_ME/.test(APP_CHECK_SITE_KEY)) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    // Init throws if called twice (HMR) — swallow silently in dev.
    if (import.meta.env.PROD) console.warn("App Check init failed:", e);
  }
}

export const auth      = getAuth(app);
export const db        = getFirestore(app);
export const functions = getFunctions(app, "us-central1");
// Cloud Storage — used by the store settings page for logo uploads, plus
// any future asset uploads (vehicle photos, etc.). Bucket comes from the
// same firebaseConfig.storageBucket above.
export const storage   = getStorage(app);

setPersistence(auth, browserLocalPersistence).catch(() => {});
